/** My Vault — owned masters: full-length player (loop/shuffle/prev/next/scrub) + downloads. */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as api from '../api';
import { VaultItem, mmss } from '../types';
import { DiscIcon, Equalizer, PageShell, PrimaryBtn, Waveform, cardPanel, s, seedFromId } from '../ui';
import { ShopTabs } from '../components/ShopTabs';

const ctrlBase = 'display:flex;align-items:center;justify-content:center;width:40px;height:40px;border-radius:10px;border:1px solid var(--bs-border-2);cursor:pointer;transition:all .12s;';

const VaultPage: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<VaultItem[] | null>(null);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [t, setT] = useState(0);
  const [loop, setLoop] = useState(false);
  const [shuffle, setShuffle] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const stateRef = useRef({ loop, shuffle, items, currentId });
  stateRef.current = { loop, shuffle, items, currentId };

  useEffect(() => {
    api.getVault().then((r) => {
      setItems(r.items);
      if (r.items.length) setCurrentId(r.items[0].beat_id);
    }).catch(() => setItems([]));
  }, []);

  const current = items?.find((i) => i.beat_id === currentId) || items?.[0] || null;
  const npDur = current ? (current.duration_seconds || 0) : 0;

  const gotoTrack = useCallback((dir: number, keepPlaying: boolean) => {
    const { items: list, currentId: cid, shuffle: sh } = stateRef.current;
    if (!list || list.length === 0) return;
    let nextId: string;
    if (sh && list.length > 1) {
      const pool = list.filter((i) => i.beat_id !== cid);
      nextId = pool[Math.floor(Math.random() * pool.length)].beat_id;
    } else {
      const idx = list.findIndex((i) => i.beat_id === cid);
      nextId = list[(idx + dir + list.length) % list.length].beat_id;
    }
    setCurrentId(nextId);
    setT(0);
    if (keepPlaying) setPlaying(true);
  }, []);

  // audio element for the current track
  useEffect(() => {
    if (!current?.stream_url) return;
    const audio = new Audio(current.stream_url);
    audioRef.current = audio;
    const onTime = () => setT(audio.currentTime);
    const onEnd = () => {
      if (stateRef.current.loop) { audio.currentTime = 0; audio.play().catch(() => {}); return; }
      const list = stateRef.current.items || [];
      if (list.length > 1) gotoTrack(1, true); else { setPlaying(false); setT(0); }
    };
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('ended', onEnd);
    if (playing) audio.play().catch(() => setPlaying(false));
    return () => {
      audio.pause();
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('ended', onEnd);
      audioRef.current = null;
    };
    // (deliberately keyed to the current track only — play/pause is handled separately)
  }, [current?.beat_id, current?.stream_url]); // eslint-disable-line

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) audio.play().catch(() => setPlaying(false));
    else audio.pause();
  }, [playing]);

  const onScrub = (frac: number) => {
    const audio = audioRef.current;
    const dur = audio?.duration || npDur;
    if (!audio || !dur) return;
    audio.currentTime = frac * dur;
    setT(audio.currentTime);
  };

  const openPurchasedFiles = (item: VaultItem) =>
    navigate(`/store/beat-shop/vault/${item.entitlement_id}`);

  const frac = npDur ? Math.min(1, t / npDur) : 0;

  return (
    <PageShell>
      <ShopTabs active="vault" />
      <div style={s('animation:bs-rise .25s ease both')}>
        <div style={s('font-size:11.5px;font-weight:600;letter-spacing:0.15em;color:var(--bs-text-label);margin-bottom:8px')}>YOUR OWNED MASTERS</div>
        <h1 className="bs-vault-heading">My Vault</h1>

        {items === null && <div style={s('color:var(--bs-text-muted)')}>Loading…</div>}

        {items !== null && items.length === 0 && (
          <div style={s('display:flex;flex-direction:column;align-items:center;justify-content:center;padding:80px 20px;text-align:center;border:1px dashed var(--bs-border-2);border-radius:18px;background:var(--bs-well)')}>
            <div style={s('margin-bottom:18px')}><DiscIcon size={42} /></div>
            <div style={s('font-weight:700;font-size:19px;color:var(--bs-text-hi)')}>Your vault is empty</div>
            <p style={s('margin:9px 0 20px;color:var(--bs-text-muted);font-size:13.5px;max-width:340px;line-height:1.5')}>
              Beats you buy land here — full WAV masters with stems, ready to play, loop and shuffle.
            </p>
            <PrimaryBtn onClick={() => navigate('/store/beat-shop')}>Go to Shop</PrimaryBtn>
          </div>
        )}

        {items !== null && items.length > 0 && current && (
          <div>
            {/* now playing */}
            <div style={s(cardPanel + 'padding:22px 24px;margin-bottom:22px;border-radius:20px')}>
              <div className="bs-vault-np">
                <div className="bs-vault-np-art" style={s('width:60px;height:60px;border-radius:13px;background:radial-gradient(40px 30px at 60% 20%, rgba(47,107,255,0.3), transparent),var(--bs-thumb);border:1px solid var(--bs-border);display:flex;align-items:center;justify-content:center;flex-shrink:0')}>
                  <DiscIcon size={26} />
                </div>
                <div className="bs-vault-np-info">
                  <div style={s('font-size:11px;font-weight:600;letter-spacing:0.12em;color:var(--bs-blue-soft);margin-bottom:4px')}>NOW PLAYING · FULL TRACK</div>
                  <div className="bs-vault-np-title">{current.name}</div>
                  <div className="bs-vault-np-meta">{current.bpm} BPM · {current.musical_key} · {current.genres.slice(0, 3).join(', ')}</div>
                </div>
                <div className="bs-vault-np-actions">
                  <button onClick={() => openPurchasedFiles(current)} style={s('display:flex;align-items:center;justify-content:center;gap:8px;padding:10px 16px;background:var(--bs-btn-bg);color:var(--bs-btn-text);border:none;border-radius:11px;font-weight:600;font-size:13px;cursor:pointer;width:100%')}>
                    <svg width="15" height="15" viewBox="0 0 18 18" fill="none" stroke="var(--bs-btn-text)" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M9 2.5v9" /><path d="M5.5 8L9 11.5 12.5 8" /><path d="M3.5 14.5h11" /></svg>
                    All purchased files
                  </button>
                </div>
              </div>

              <div style={s('margin-bottom:12px;width:100%;max-width:100%;min-width:0;overflow:hidden')}>
                <Waveform seed={seedFromId(current.beat_id) * 211 + 5} count={90} height={64} progress={frac} onScrub={onScrub} />
              </div>
              <div style={s('display:flex;align-items:center;justify-content:space-between;font-size:12px;color:var(--bs-text-muted);margin-bottom:18px;font-variant-numeric:tabular-nums')}>
                <span>{mmss(t)}</span>
                <span>{npDur ? mmss(npDur) : '—'}</span>
              </div>

              <div className="bs-vault-controls">
                <button onClick={() => setShuffle(!shuffle)} title="Shuffle"
                  style={s(ctrlBase + (shuffle ? 'background:rgba(47,107,255,0.14);border-color:rgba(47,107,255,0.5);color:#2F6BFF' : 'background:transparent;color:var(--bs-text-mid)'))}>
                  <svg width="19" height="19" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M3 4.5h3.2c1 0 1.9.5 2.4 1.3l2.8 4.4c.5.8 1.4 1.3 2.4 1.3H17" /><path d="M3 15.5h3.2c1 0 1.9-.5 2.4-1.3l.9-1.4" /><path d="M12 6.2l1.3-1.7H17" /><path d="M14.6 3l2.4 1.5L14.6 6" /><path d="M14.6 12l2.4 1.5-2.4 1.5" /><path d="M11.4 9.6l1.9 2.9H17" /></svg>
                </button>
                <button onClick={() => gotoTrack(-1, playing)} title="Previous" style={s('display:flex;align-items:center;justify-content:center;width:44px;height:44px;border-radius:50%;border:none;background:transparent;color:var(--bs-text-body);cursor:pointer')}>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor"><path d="M6 4v12H4V4z" /><path d="M16 4v12l-9-6z" /></svg>
                </button>
                <button onClick={() => setPlaying(!playing)} style={s('width:60px;height:60px;border-radius:50%;background:var(--bs-btn-bg);border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;flex-shrink:0')}>
                  {playing ? (
                    <svg width="18" height="18" viewBox="0 0 16 16" fill="var(--bs-btn-text)"><rect x="3" y="2" width="3.5" height="12" rx="1" /><rect x="9.5" y="2" width="3.5" height="12" rx="1" /></svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 16 16" fill="var(--bs-btn-text)"><path d="M4 2.5v11l9-5.5z" /></svg>
                  )}
                </button>
                <button onClick={() => gotoTrack(1, playing)} title="Next" style={s('display:flex;align-items:center;justify-content:center;width:44px;height:44px;border-radius:50%;border:none;background:transparent;color:var(--bs-text-body);cursor:pointer')}>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor"><path d="M14 4v12h2V4z" /><path d="M4 4v12l9-6z" /></svg>
                </button>
                <button onClick={() => setLoop(!loop)} title="Loop"
                  style={s(ctrlBase + (loop ? 'background:rgba(47,107,255,0.14);border-color:rgba(47,107,255,0.5);color:#2F6BFF' : 'background:transparent;color:var(--bs-text-mid)'))}>
                  <svg width="19" height="19" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M5 7.5h8.5A3.5 3.5 0 0 1 17 11v.5A3.5 3.5 0 0 1 13.5 15H6.5A3.5 3.5 0 0 1 3 11.5" /><path d="M7 5L4.5 7.5 7 10" /></svg>
                </button>
              </div>
            </div>

            {/* track list */}
            <div className="bs-vault-list-head">
              <span>#</span>
              <span>TRACK</span>
              <span className="bs-vault-col-desktop">TEMPO</span>
              <span className="bs-vault-col-desktop">KEY</span>
              <span className="bs-vault-col-desktop">LENGTH</span>
              <span />
            </div>
            {items.map((v, i) => {
              const isCurrent = v.beat_id === current.beat_id;
              return (
                <div key={v.entitlement_id} className={'bs-vrow' + (isCurrent ? ' bs-vrow-active' : '')}
                  onClick={() => { setCurrentId(v.beat_id); setT(0); setPlaying(true); }}>
                  <div className="bs-vrow-idx">
                    {isCurrent && playing ? <Equalizer height={14} /> : <span style={s('font-size:13px;color:var(--bs-text-dim);font-variant-numeric:tabular-nums')}>{i + 1}</span>}
                  </div>
                  <div className="bs-vrow-track">
                    <div className="bs-vrow-thumb"><DiscIcon size={18} /></div>
                    <div style={s('min-width:0;flex:1')}>
                      <div className="bs-vrow-name" style={{ color: isCurrent ? 'var(--bs-blue-soft)' : 'var(--bs-text)' }}>{v.name}</div>
                      <div className="bs-vrow-sub">{v.bpm} BPM · {v.musical_key} · {v.duration_seconds ? mmss(v.duration_seconds) : '—'}{v.genres.length ? ` · ${v.genres.slice(0, 2).join(' · ')}` : ''}</div>
                      <div className="bs-vrow-genres-desktop">{v.genres.slice(0, 3).join(' · ')}</div>
                    </div>
                  </div>
                  <span className="bs-vault-col-desktop bs-vrow-cell">{v.bpm} BPM</span>
                  <span className="bs-vault-col-desktop bs-vrow-cell">{v.musical_key}</span>
                  <span className="bs-vault-col-desktop bs-vrow-cell">{v.duration_seconds ? mmss(v.duration_seconds) : '—'}</span>
                  <div className="bs-vrow-dl">
                    <button onClick={(e) => { e.stopPropagation(); openPurchasedFiles(v); }} title="View and download all purchased files"
                      style={s('background:none;border:none;color:var(--bs-text-faint);cursor:pointer;padding:6px')}>
                      <svg width="16" height="16" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M9 2.5v9" /><path d="M5.5 8L9 11.5 12.5 8" /><path d="M3.5 14.5h11" /></svg>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </PageShell>
  );
};

export default VaultPage;
